<template>
  <span>
    <div class="page-level-functions">
      <action-button
        variant="success float-right ml-3"
        text="Solicitar Cuenta"
        i="fas fa-file-plus"
        @click="goToRequestCreation"
      />

      <action-button
        v-if="hasNavigatePermission"
        variant="outline-success float-right ml-3"
        text="Ver Usuarios"
        i="fas fas fa-user"
        @click="goToUsers()"
      />
      <action-button
        variant="outline-success float-right ml-3"
        text="Regresar"
        i="fas fa-arrow-circle-left"
        @click="back"
      />
    </div>

    <hr class="page-level-functions-separator" />

    <div>
      <filter-manager
        class="rounded"
        v-model="search"
        :filters="filtersConfig"
        search-container-class="search-custom"
        @search="onClickSearchHandler"
        @application="onChangedApplicationsHandler($event)"
        @level="onChangeLevelHandler($event)"
        @region="onChangeRegionHandler($event)"
        @municipality="onChangeMunicipalityHandler($event)"
      />

      <custom-separator class="my-4" text="Resultado de Solicitud de Cuenta" />

      <custom-result-not-found v-if="results.length === 0" />

      <div v-for="(row, index) in results" :key="`row-app-${index}`">
        <result-row
          :ref="`result-${index}`"
          v-model="results[index]"
          :has-actions-permission="hasActionsPermission"
          @on-change-status="(...args) => changeStatusHandler(...args, index)"
          @on-open-close="openCloseResultHandler($event, index)"
        />
      </div>

      <template v-if="showPagination">
        <pagination
          show-text
          @pagination-go-page="goToPageHandler"
          :data="pagination"
        />
      </template>
    </div>
  </span>
</template>

<script>
import FilterManager from 'FilterManager';
import Pagination from 'Pagination';
import CustomSeparator from 'custom-separator';
import { ActionButton } from '@/components/common';
import CustomResultNotFound from 'custom-result-not-found';
import ResultRow from '../components/ResultRow.vue';
import userRequestStatuses from '@/utils/constants/userRequestStatuses';
import alert from 'utils/mixins/alert.mixin';
import { userMixin } from '@/modules/user/mixins/user.mixin.js';
import storeMixin from '@/mixins/storeGenerator';

import {
  getFiscalYears,
  getStatuses,
  getUserRequests,
  changeRequestStatus as _changeRequestStatus,
} from '@/modules/userRequest/api';
import { addUserFromRequest } from '@/modules/users/api';
import eduespService from '@/services/eduesp';
import { mapState } from 'vuex';
import { name as userModuleName } from '@/modules/user/store';
import { getApplicationsList } from '@/modules/applications/api';
import { getRolesByApplicattionId } from '@/modules/roles/api';
import locationAccessLevels from '@/utils/constants/locationAccessLevels.js';
import InterfacesServices from '@/modules/interfaces/api';
import deticketsService from '@/services/detickets';
import apps from '@/utils/constants/applicationIds';

const initialState = () => ({
  search: {},
  options: {
    status: [],
    academicYears: [],
    applications: [],
    roles: [],
    levels: [],
    centers: [],
    regions: [],
    municipalities: [],
    schools: [],
  },
  results: [],
  pagination: {
    totalItemCount: 0,
    count: 15,
    pageCount: 0,
    currentPage: 1,
  },
  selectedRequest: {},
});

export default {
  name: 'UserSearch',
  components: {
    FilterManager,
    CustomSeparator,
    ActionButton,
    CustomResultNotFound,
    Pagination,
    ResultRow,
  },
  mixins: [alert, userMixin, storeMixin],
  data() {
    return {
      ...initialState(),
    };
  },
  computed: {
    ...mapState(userModuleName, ['user']),
    hasActionsPermission() {
      return true;
    },
    hasNavigatePermission() {
      return this.userHasPermissions('security.user.redirection');
    },
    showPagination() {
      return (
        this.results.length > 0 &&
        this.pagination.totalItemCount > this.pagination.count
      );
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.options.academicYears,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: false,
          class: 'col-12 col-md-2',
          initialValue: this.search.academicYear,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.options.status,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-2',
          clearable: true,
        },
        {
          name: 'application',
          component: 'CustomDropDown',
          options: this.options.applications,
          placeholder: 'Aplicación',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-2',
          clearable: true,
        },
        {
          name: 'role',
          component: 'CustomDropDown',
          options: this.options.roles,
          placeholder: 'Rol',
          fieldName: 'roleName',
          fieldKey: 'roleId',
          class: 'col-12 col-md-2',
          clearable: true,
        },
        {
          name: 'inputText',
          component: 'CustomInput',
          icon: 'fa fa-search color-orange',
          leftIcon: true,
          placeholder: 'Escriba usuario, nombre o email',
          class: 'col-12 col-md-4',
        },
        {
          name: 'level',
          component: 'CustomDropDown',
          options: this.options.levels,
          label: 'Nivel:',
          placeholder: 'Seleccione',
          fieldName: 'displayName',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'center',
          component: 'CustomDropDown',
          options: this.options.centers,
          label: `${this.getDisplayLevelName(this.search.level?.name)}:`,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
          visible:
            this.search.level != undefined && !this.showLocationSchoolFilters,
        },

        {
          name: 'region',
          component: 'CustomDropDown',
          options: this.options.regions,
          label: 'Región:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
          visible: this.showLocationSchoolFilters,
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.options.municipalities,
          label: 'Municipio:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
          visible: this.showLocationSchoolFilters,
        },
        {
          name: 'school',
          component: 'CustomDropDown',
          options: this.options.schools,
          label: 'Escuela:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
          visible: this.showLocationSchoolFilters,
        },
      ];
    },
    getDisplayLevelName() {
      return (name) => {
        if (name === locationAccessLevels.Municipality) return 'Municipio';
        else if (name === locationAccessLevels.Region) return 'Región';
        else if (name === locationAccessLevels.District) return 'Distrito';
        else if (name === locationAccessLevels.Center) return 'CSEE';
        else if (name === locationAccessLevels.School) return 'Escuela';
        else if (name === locationAccessLevels.Company) return 'Compañía';
        else if (name === locationAccessLevels.CompanySpecialist) return 'Compañía Especialista';
        else if (name === locationAccessLevels.Student) return 'Estudiante';
        else return '-';
      };
    },
    showLocationSchoolFilters() {
      return this.search.level?.name === locationAccessLevels.School;
    },
    schoolsFilter() {
      return this.options.schools.length > 0
        ? this.options.schools.map((x) => parseInt(x.id)).join()
        : null;
    },
  },
  beforeRouteEnter(_, from, next) {
    next((vm) => {
      if (from.name == 'home') {
        vm.shouldRecovery = false;
        const dataSaved = initialState();
        vm.search = dataSaved.search;
        vm.options = dataSaved.options;
        vm.results = dataSaved.results;
        vm.pagination = dataSaved.pagination;
        vm.selectedRequest = dataSaved.selectedRequest;
      } else vm.recoveryData();

      vm.fetchResults(vm.pagination.currentPage);
    });
  },
  async mounted() {
    await Promise.all([
      this.fetchFiscalYears(),
      this.fetchStatuses(),
      this.fetchApplications(),
      this.fetchLevels(),
      this.fetchResults(),
    ]);
  },
  destroyed() {
    this.persistData(['results', 'pagination'], true);
  },
  methods: {
    async fetchApplications() {
      await getApplicationsList().then(
        (res) => (this.options.applications = res)
      );
    },
    async fetchLevels() {
      this.options.levels = (await InterfacesServices.getLevels()).data.map(
        (x) => {
          x.displayName = this.getDisplayLevelName(x.name);
          return x;
        }
      );
    },
    async fetchFiscalYears() {
      const data = await getFiscalYears();
      this.options.academicYears = [...data];

      const selected = data.find((x) => x.selected);
      if (selected) {
        this.search.academicYear = selected;
      }
    },
    async fetchStatuses() {
      const data = await getStatuses();
      this.options.status = [...data];
    },
    async onChangedApplicationsHandler(app) {
      this.search.role = null;

      if (!app) {
        return;
      }

      await this.getRoles(app.id);
    },

    async getRoles(appId) {
      await getRolesByApplicattionId(appId).then((res) => {
        const items = res.data;
        this.options.roles = items;

        if (items.length == 1) {
          this.search.role = items[0];
        }
      });
    },
    async onChangeLevelHandler(value) {
      this.search.center = null;

      if (this.showLocationSchoolFilters) {
        this.options.regions = await eduespService.getRegions();
        return;
      }

      if (value?.id) {
        let { data } = await InterfacesServices.getLevelCenters(value?.name);
        this.options.centers = data;
      }

      this.clearSchoolLocationFilters();
    },
    async onChangeRegionHandler(value) {
      if (!value) {
        this.options.municipalities = [];
        this.options.schools = [];
        this.search.municipality = null;
        this.search.school = null;
        return;
      }
      this.search.municipality = null;
      this.search.school = null;
      this.options.municipalities = await eduespService.getMunicipalities({
        regionId: value.id,
      });
      this.options.schools = await eduespService.getSchools({
        regionId: value.id,
      });
    },
    async onChangeMunicipalityHandler(value) {
      if (!value) {
        this.options.schools = [];
        this.search.school = null;
        this.onChangeRegion(this.search.region);
        return;
      }

      this.search.school = null;
      this.options.schools = await eduespService.getSchools({
        municipalityId: value.id,
      });
    },
    clearSchoolLocationFilters() {
      this.search.region = null;
      this.search.municipality = null;
      this.search.school = null;
    },
    onClickSearchHandler() {
      this.fetchResults(1);
    },
    async fetchResults(page) {
      const filters = {
        academicYearId: this.search.academicYear?.id,
        statusId: this.search.status?.id,
        applicationId: this.search.application?.id,
        roleId: this.search.role?.roleId,
        levelId: this.search.level?.id ?? null,
        centerId: this.showLocationSchoolFilters
          ? this.search.school?.id
          : this.search.center?.id ?? null,
        schools: this.schoolsFilter,
        searchField: this.search.inputText,
        pageIndex: page || this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
        userId: this.hasActionsPermission ? null : this.user.profile.sub,
      };

      const results = await getUserRequests(filters);

      if (results) {
        const { count, currentPage, data, pageCount, totalItemCount } = results;

        this.results = [...data];
        this.pagination.totalItemCount = totalItemCount;
        this.pagination.count = count;
        this.pagination.pageCount = pageCount;
        this.pagination.currentPage = currentPage;
      }
    },
    goToPageHandler(page) {
      this.fetchResults(page);
    },
    viewUser(path) {
      this.$router.push({ path });
    },
    back() {
      this.$router.push({ name: 'home' });
    },
    goToUsers() {
      this.$router.push({ name: 'users', params: { from: 'UserRequest' } });
    },
    goToRequestCreation(requestId = null) {
      this.$router.push({ name: 'ManageRequest', params: { id: requestId } });
    },
    async changeStatusHandler(request, userModel, rowIdx) {
      this.selectedRequest = request;
      let isApprovingMode = false;
      let verbs = {
        confirmQuestion: null,
        successMsg: null,
      };

      switch (this.selectedRequest.statusId) {
        case userRequestStatuses.Aprobada:
          verbs.confirmQuestion = 'aprobar';
          verbs.successMsg = 'aprobada';
          isApprovingMode = true;
          break;
        case userRequestStatuses.Cancelada:
          verbs.confirmQuestion = 'cancelar';
          verbs.successMsg = 'cancelada';
          break;
        case userRequestStatuses.Denegada:
          verbs.confirmQuestion = 'denegar';
          verbs.successMsg = 'denegada';
          break;
      }

      const answer = await this.ShowCancelModal(
        'Atención',
        `¿Está seguro que desea ${verbs.confirmQuestion} la solicitud de la creación de usuario?`,
        'Sí',
        'No'
      );
      if (!answer) return;

      if (!isApprovingMode) {
        await this.changeRequestStatus(this.selectedRequest, verbs.successMsg);
        return;
      }

      await addUserFromRequest(
        this.selectedRequest.requestAccountUserId,
        userModel.username,
        userModel.authMode
      )
        .then(async ({ data }) => {
          this.selectedRequest.createdUserId = data.userId;
          await eduespService.createUserProfile(data);

          if (data.requireDETicketsProfile) {
            const profile = {
              userId: data.userId,
              firstName: data.firstName,
              lastName1: data.lastPaternalName,
              lastName2: data.lastMaternalName,
              deptIds: data.deptIds,
              socialSecurityNumber: data.socialSecurityNumber,
              isActiveDirectoyUser: data.isActiveDirectoryUser,
              email: data.email,
              createsCasesOnlyForSelf: false,
              applicationId: apps.detickets,
            };
            await deticketsService.createUserProfile(profile);
          }
          await this.changeRequestStatus(
            this.selectedRequest,
            verbs.successMsg
          );
        })
        .catch((err) => {
          const errors = err.response?.data;
          const inputErrors = { usernameVP: [] };
          const errorMsg = 'Ocurrio un error creando el usuario.';

          errors.value.forEach((error) => {
            if (error.value == 6) {
              inputErrors.usernameVP.push(error.message);
            }
          });
          this.$refs[`result-${rowIdx}`][0].setErrors(inputErrors);

          this.Toast('Error', errors.value[0]?.message || errorMsg, 'error');
        });
    },
    async changeRequestStatus(model, verb) {
      await _changeRequestStatus(model)
        .then((data) => {
          if (data.response && data.response.status != 200) {
            this.Toast(
              'Error',
              data.response?.data?.message ?? 'Error con la solicitud',
              'error'
            );
          } else {
            this.Toast(
              'Proceso Exitoso',
              `La solicitud ha sido ${verb} exitosamente.`,
              'success'
            );
          }
        })
        .then(() => this.fetchResults());
    },
    openCloseResultHandler(value, idx) {
      this.results = this.results.map((r, i) => ({
        ...r,
        isOpen: i === idx ? !value : false,
      }));
    },
  },
};
</script>

<style scoped lang="scss">
/*** page level functions container **/
.page-level-functions {
  display: inline-block !important;
  vertical-align: bottom !important;
  width: 50%;
}

hr.page-level-functions-separator {
  margin: 20px -45px;
  border: 1px solid #e3e3e3;
  border-bottom: none;
}
</style>

<style>
.search-custom {
  -ms-flex: 0 0 16.6666666667% !important;
  flex: 0 0 16.6666666667% !important;
  max-width: 16.6666666667% !important;
}
</style>
